// MPD-118 (MPD-115 #1): a saved card stored before MPD-10 item 9 still holds the old source wording
// ("Heart daily sales", "Heart sales data", the Swedish forms). legacySourceText.json is an identical
// copy of app/api/data/legacy_source_text.json, the map the API serves saved boards with; tests on both
// sides keep the two equal. The web applies it too, so a board restored from this browser (the legacy
// local backup, an unsynced edit) and a card that never refreshes or failed to refresh read neutral.
// Whole phrases only, longest first: the word Heart alone is never replaced, so a brand or product
// name that contains it reads as stored. No migration: the neutral text is stored only by the owner's
// next normal save of the board (MPD-122).
import map from './legacySourceText.json'
import type { DashboardCard } from './types'

const PHRASES: Readonly<Record<string, string>> = map.phrases
const WORD = /^[\p{L}\p{N}_]$/u
const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
// A phrase that starts or ends inside a word ("SweetHeart daily sales…") is not that phrase.
const PATTERN = new RegExp(Object.keys(PHRASES).sort((a, b) => b.length - a.length).map((phrase) =>
  (WORD.test(phrase[0]) ? '(?<![\\p{L}\\p{N}_])' : '') + escape(phrase)
  + (WORD.test(phrase[phrase.length - 1]) ? '(?![\\p{L}\\p{N}_])' : '')).join('|'), 'gu')

/** `value` with every old source phrase replaced, walking objects and arrays (a copy). An object's
 *  `spec` (machine-only, used to refresh) is kept as it is. */
export function neutralLegacyText<T>(value: T): T {
  if (typeof value === 'string') return value.replace(PATTERN, (phrase) => PHRASES[phrase] ?? phrase) as T
  if (Array.isArray(value)) return value.map(neutralLegacyText) as T
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) =>
      [key, key === 'spec' ? item : neutralLegacyText(item)])) as T
  }
  return value
}

/** A saved card as shown: its stored answer in the neutral wording. The title and the user's own
 *  text and action cards are the user's words and stay. */
export function neutralSavedCard<C extends DashboardCard>(card: C): C {
  return card.type === 'chart' ? { ...card, last_contract: neutralLegacyText(card.last_contract) } : card
}
